import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

const LANGUAGES = [
    { code: 'en', label: 'EN', name: 'English' },
    { code: 'fr', label: 'FR', name: 'French' },
    { code: 'de', label: 'DE', name: 'German' },
    { code: 'es', label: 'ES', name: 'Spanish' },
];

/**
 * TranslationButtons - Instagram/Twitter-style translation buttons
 *
 * Props:
 * - objectId: ID of the news or article
 * - objectType: 'news' or 'article'
 * - translations: Cached translations object from the item
 * - activeLanguage: Currently active translation language (or null for original)
 * - onTranslate: Callback when translation is requested (language) => {}
 * - onClearTranslation: Callback to clear/toggle off translation
 * - loading: Boolean indicating if translation is in progress
 * - loadingLanguage: Which language is currently being loaded
 * - collapsible: If true, language buttons are hidden until the globe icon is clicked
 */
@observer
class TranslationButtons extends Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false,
        };
    }

    toggleExpanded = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState(prevState => ({ expanded: !prevState.expanded }));
    }

    handleLanguageClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { code } = e.target.closest('button').dataset;
        const { activeLanguage, onTranslate, onClearTranslation } = this.props;

        if (activeLanguage === code) {
            // Toggle off - return to original
            if (onClearTranslation) {
                onClearTranslation();
            }
        } else {
            // Request translation
            if (onTranslate) {
                onTranslate(code);
            }
        }
    }

    render() {
        const {
            translations = {},
            activeLanguage,
            loading,
            loadingLanguage,
            collapsible,
        } = this.props;
        const { expanded } = this.state;
        const showLanguages = !collapsible || expanded;

        return (
            <small class='d-flex flex-row align-items-center'>
                {collapsible ? (
                    <button
                        type='button'
                        class='btn btn-link p-0 text-reset'
                        onClick={this.toggleExpanded}
                        title='Translate'
                    >
                        <i class={`fa-${activeLanguage ? 'solid' : 'light'} fa-globe me-1`} />
                    </button>
                ) : (
                    <i class={`fa-${activeLanguage ? 'solid' : 'light'} fa-globe me-1`} />
                )}
                {showLanguages && LANGUAGES.map(lang => {
                    const isCached = translations && translations[lang.code] && translations[lang.code].translatedDate;
                    const isActive = activeLanguage === lang.code;
                    const isLoading = loading && loadingLanguage === lang.code;

                    return (
                        <button
                            type='button'
                            class={`btn btn-link p-0 ms-1 ${isActive ? '' : 'text-muted'}`}
                            style={isActive ? 'text-decoration: underline;' : ''}
                            data-code={lang.code}
                            onClick={this.handleLanguageClick}
                            title={`Translate to ${lang.name}${isCached ? ' (cached)' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <i class='fa-solid fa-spinner fa-spin' />
                            ) : (
                                <span style={isCached && !isActive ? 'font-weight: 500;' : ''}>{lang.label}</span>
                            )}
                        </button>
                    );
                })}
            </small>
        );
    }
}

export default TranslationButtons;
